import { lazy, ReactNode, Suspense, useEffect } from "react";

import {
  ClassificationIcon,
  FileSize,
  Icon,
  LoadingIndicator,
  NoData,
  RenderWhen
} from "../../../../components";
import { MenuItems } from "../../../../components/@types";
import ActionsMenu from "../../../../components/ActionsMenu";
import ActionsModal, {
  ActionsModalNames
} from "../../../../components/ActionsModal";
import SortablePaginatedGrid from "../../../../components/Grid";
import GridRowExpandable from "../../../../components/GridRowExpandable";
import TagsModal from "../../../../components/tags/TagsModal";
import {
  useDataModal,
  useExpandableRowsGrid,
  useSelectRowsContext
} from "../../../../hooks";
import useModal from "../../../../hooks/useModal";
import { NotificationsLevel } from "../../../../lib/enums";
import * as notifications from "../../../../notifications/notifications";
import api from "../../../../services/api";
import { consts, enums, route, settings } from "../../../../shim/jsbridge";
import { ExtractedFilesResultItem, Schema } from "../../@types";
import { VIEW_TYPES } from "../../containers/ExtractedFilesContainer";
import { createSelectedRowKey, isEmpty } from "../../util";
import ExtractedFilesAuxiliaryNameCell from "./ExtractedFilesAuxiliaryNameCell";
import ExtractedFilesFileNameCell from "./ExtractedFilesFileNameCell";
import ExtractedFilesFlatNameCell from "./ExtractedFilesFlatNameCell";
import ExtractedFilesTableFileCount from "./ExtractedFilesTableFileCount";
import useTagAllExtracted from "./hooks/useTagAllExtracted";
import { filterValidHashes } from "./utils";
import { copyToUploads } from "../../../search/services";
import { getSampleDisplayNameFromSchemaData } from "../../../../utils";

const {
  state: { ERROR }
} = enums || {};

const ExpandedRow = lazy(
  () => import("../expanded_details/containers/ExpandedDetails")
);

type CallbackValue = {
  value: ExtractedFilesResultItem;
};

type Props = {
  results: Array<ExtractedFilesResultItem>;
  count: number;
  isLoading: boolean;
  gridErrorMsg: string;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  hashValue: string;
  sampleSourceId: string;
  sampleSourceHash: string;
  extractedFilesQP: Record<string, unknown>;
  onDirectoryClick: (value: CallbackValue) => void;
  onClassificationChange: () => void;
  onViewHex: (value: CallbackValue) => void;
  viewType: keyof typeof VIEW_TYPES;
  auxiliarySourceId?: string;
  emptyGridMsg?: ReactNode;
  sort?: SortState;
  onSortChange?: (sort: SortState) => void;
};

type SortState = {
  field: string;
  direction: "asc" | "desc";
};

const DEFAULT_EMPTY_GRID_MSG = (
  <div className="flex align-center justify-content-center m-top-20 m-bottom-20">
    No results were found for the selected filter criteria.
  </div>
);

const ExtractedFilesTable = ({
  results,
  count,
  isLoading,
  gridErrorMsg,
  page,
  pageSize = 20,
  onPageChange,
  hashValue,
  sampleSourceId,
  sampleSourceHash,
  extractedFilesQP,
  onDirectoryClick,
  onClassificationChange,
  onViewHex,
  viewType,
  auxiliarySourceId,
  emptyGridMsg = DEFAULT_EMPTY_GRID_MSG,
  sort,
  onSortChange
}: Props) => {
  const enableSort = Boolean(onSortChange);
  const { data: modal, onToggle } = useDataModal();
  const [showTagsModal, , toggleTagsModal] = useModal();
  const { mutate } = useTagAllExtracted(hashValue);

  const { isSelectAll, selected, api: selectApi } = useSelectRowsContext();
  const { expandedRows, handleCheckRowExpanded, handleRowExpandedChange } =
    useExpandableRowsGrid(page);

  const handleTagAll = (selectedTags: MenuItems) => {
    const tags = selectedTags.map(tag => tag.value);
    mutate(tags);
  };

  const onToggleModal = ({
    show,
    name,
    modalData
  }: {
    show: boolean;
    name: string;
    modalData: unknown;
  }) => {
    onToggle(modalData, show ? name : "");
  };

  const handleApplyTags = ({
    applyTagHashes
  }: {
    applyTagHashes: string[];
  }) => {
    if (isSelectAll) {
      toggleTagsModal();
      return;
    }
    onToggleModal({
      show: true,
      name: ActionsModalNames.ApplyTags,
      modalData: {
        hashes: applyTagHashes
      }
    });
  };

  const handleSelectRowChange = (
    item: ExtractedFilesResultItem,
    index: number
  ) => {
    selectApi.select(item, index, page);
  };

  const handleSelectPage = () => {
    selectApi.selectPage(results, page);
  };

  const checkRowSelected = (item: ExtractedFilesResultItem, rowIndex: number) =>
    selectApi.checkIsSelected(item, rowIndex, page);

  const handleRowExpand = (
    isExpanded: boolean,
    rowItem: ExtractedFilesResultItem,
    rowIndex: number
  ) => {
    if (!rowItem?.sample?.sha1) {
      onDirectoryClick({ value: rowItem });
      return;
    }

    handleRowExpandedChange(isExpanded, rowItem, rowIndex);
  };

  useEffect(() => {
    if (isSelectAll) {
      selectApi.selectAll(results, page);
    }
  }, [isSelectAll, results, page]);

  const gridSchema: Schema<ExtractedFilesResultItem> = {
    fields: [
      {
        name: "status",
        displayTitle: "",
        sortable: false,
        component: d => (
          <ClassificationIcon
            classification={d?.sample?.classification}
            riskScore={d?.sample?.riskscore}
            directory={!d?.sample?.sha1}
          />
        ),
        className: "threat-table-cell",
        style: {
          width: "40px"
        }
      },
      {
        name: "threat_name",
        displayTitle: "Threat",
        sortable: enableSort,
        component: d => d?.sample?.classification_result || "--",
        className: "overflow-ellipsis",
        style: {
          width: "22%"
        }
      },
      {
        name: "file_name",
        displayTitle: "Name",
        sortable: enableSort,
        className: "overflow-ellipsis",
        component: d => {
          if (viewType === VIEW_TYPES.FLAT.value) {
            if (auxiliarySourceId) {
              return (
                <ExtractedFilesAuxiliaryNameCell
                  fileName={d?.full_path ?? ""}
                  sha1={d?.sample?.sha1}
                  classification={d?.sample?.classification}
                  auxiliarySourceId={auxiliarySourceId}
                  sampleId={d?.id}
                  processingStatus={d?.processing_status}
                />
              );
            }
            return (
              <ExtractedFilesFlatNameCell
                id={d?.id?.toString()}
                fileName={d?.full_path ?? ""}
                sha1={d?.sample?.sha1}
                sourceId={sampleSourceId}
                classification={d?.sample?.classification}
              />
            );
          }
          return (
            <ExtractedFilesFileNameCell
              fileName={d?.filename}
              id={d?.id}
              sha1={d?.sample.sha1}
              sampleSourceId={sampleSourceId}
              onClick={e => {
                e.stopPropagation();
                if (!d?.sample?.sha1) {
                  onDirectoryClick({ value: d });
                }
              }}
            />
          );
        },
        style: {
          width: "42%"
        }
      },
      {
        name: "sample_type",
        displayTitle: "Format",
        sortable: enableSort,
        className: "overflow-ellipsis",
        style: {
          width: "20%"
        },
        component: d => d?.sample?.type_display || <NoData />
      },
      {
        name: "file_count",
        displayTitle: "Files",
        sortable: enableSort,
        className: "numeric-cell",
        style: {
          width: "6%",
          textAlign: "center"
        },
        component: d => (
          <ExtractedFilesTableFileCount
            isLoading={isLoading}
            sha1={d.sample?.sha1}
            sampleSourceId={sampleSourceId}
            fileCount={d.sample?.extracted_file_count}
            relationship={d.id}
            href={
              auxiliarySourceId && d.sample?.sha1
                ? route(
                    "redirect_samples_details_section",
                    {
                      hash_value: d.sample.sha1,
                      section: "extracted-files"
                    },
                    { auxiliary_source_id: auxiliarySourceId }
                  )
                : undefined
            }
            onClick={e => {
              e.stopPropagation();
            }}
          />
        )
      },
      {
        name: "sample_size",
        displayTitle: "Size",
        sortable: enableSort,
        className: "numeric-cell",
        style: {
          width: "75px",
          textAlign: "center"
        },
        component: d =>
          !d?.sample?.file_size ? (
            "--"
          ) : (
            <FileSize value={d?.sample?.file_size} />
          )
      },
      {
        name: "_actions",
        displayTitle: () => {
          if (!isSelectAll && isEmpty(selected)) {
            return null;
          }

          const actions = [];

          if (settings.TCBASE_TYPE === "a1000") {
            const applyTagHashes = isSelectAll
              ? []
              : filterValidHashes(selected).map(
                  (item: ExtractedFilesResultItem) => item.sample?.sha1
                );

            actions.push({
              label: "Apply Tags",
              disabled: !(isSelectAll || !!applyTagHashes?.length),
              onClick: () => handleApplyTags({ applyTagHashes })
            });
          }

          if (settings.FEATURE_ALERTFEED) {
            const selectAllQuery = {
              hash: sampleSourceHash,
              type: "extracted-files",
              ...extractedFilesQP,
              location: "local",
              page: 1,
              page_size: consts.max_number_subscription_hashes,
              auxiliary_source_id: auxiliarySourceId
            };

            const subscriptionHashes = filterValidHashes(selected).map(
              (item: ExtractedFilesResultItem) => ({
                sha1: item.sample?.sha1,
                available: true,
                location: "local"
              })
            );
            actions.push({
              label: `Subscribe${
                isSelectAll && count > consts.max_number_subscription_hashes
                  ? ` (up to ${consts.max_number_subscription_hashes})`
                  : ""
              }`,
              disabled: !(isSelectAll || !!subscriptionHashes?.length),
              onClick: () => {
                onToggleModal({
                  show: true,
                  name: ActionsModalNames.Subscribehashes,
                  modalData: {
                    subscriptionHashes,
                    extractedSampleQuery: isSelectAll
                      ? selectAllQuery
                      : undefined,
                    action: "subscribe"
                  }
                });
              }
            });

            const unsubscriptionHashes = filterValidHashes(selected).map(
              (item: ExtractedFilesResultItem) => item.sample?.sha1
            );
            actions.push({
              label: `Unsubscribe${
                isSelectAll && count > consts.max_number_subscription_hashes
                  ? ` (up to ${consts.max_number_subscription_hashes})`
                  : ""
              }`,
              disabled: !(isSelectAll || !!unsubscriptionHashes?.length),
              onClick: () =>
                onToggleModal({
                  show: true,
                  name: ActionsModalNames.Unsubscribehashes,
                  modalData: {
                    subscriptionHashes: unsubscriptionHashes,
                    extractedSampleQuery: isSelectAll
                      ? selectAllQuery
                      : undefined,
                    action: "unsubscribe"
                  }
                })
            });
          }

          return (
            <ActionsMenu
              className="highlight samples-grid__actions-column"
              id="extracted-files-grid-bulk-actions"
              actions={actions}
            />
          );
        },
        style: {
          width: "3%"
        },
        component: (d, idx) => {
          if (!d?.sample?.sha1) return undefined;
          if (auxiliarySourceId && (!d?.id || d?.processing_status === ERROR))
            return undefined;
          const actions = [
            {
              label: "View HEX",
              onClick: () => onViewHex({ value: d })
            },
            {
              label: "Copy To My Submissions",
              onClick: () =>
                copyToUploads(
                  d?.sample?.sha1,
                  getSampleDisplayNameFromSchemaData(d?.sample)
                )
            },
            {
              label: "Download Sample",
              onClick: () =>
                onToggleModal({
                  show: true,
                  modalData: d?.sample,
                  name: ActionsModalNames.Download
                })
            },

            {
              label: "Set Classification",
              onClick: () =>
                onToggleModal({
                  show: true,
                  name: ActionsModalNames.UserClassification,
                  modalData: d?.sample
                })
            },
            {
              label: "Reanalyze",
              onClick: () => {
                onToggleModal({
                  show: true,
                  name: ActionsModalNames.Reanalyze,
                  modalData: {
                    samples: [
                      auxiliarySourceId
                        ? { hash_value: d?.sample?.sha1 }
                        : {
                            hash_value: d?.sample?.sha1,
                            source_id: sampleSourceId,
                            relationship_id: d?.id
                          }
                    ]
                  }
                });
              }
            }
          ];

          if (d.path) {
            actions.push({
              label: "Download Container",
              onClick: () => {
                if (auxiliarySourceId) {
                  onToggleModal({
                    show: true,
                    modalData: {
                      sha1: auxiliarySourceId,
                      file_names: [auxiliarySourceId],
                      isContainer: true
                    },
                    name: ActionsModalNames.Download
                  });
                  return;
                }
                api
                  .getTopContainer({ hash: d.sample.sha1 })
                  .then(({ data: containerData }) => {
                    const containerSha1 = containerData.container_sha1;
                    onToggleModal({
                      show: true,
                      modalData: {
                        sha1: containerSha1,
                        file_names: [containerSha1]
                      },
                      name: ActionsModalNames.Download
                    });
                  })
                  .catch(() => {
                    notifications.show({
                      level: NotificationsLevel.ERROR,
                      message: "Download Container Failed"
                    });
                  });
              }
            });
          }

          if (settings.FEATURE_ALERTFEED) {
            actions.push(
              {
                label: "Subscribe",
                onClick: () => {
                  const subscribeData = {
                    sha1: d?.sample?.sha1,
                    available: true,
                    location: "local"
                  };
                  onToggleModal({
                    show: true,
                    name: ActionsModalNames.Subscribehashes,
                    modalData: {
                      subscriptionHashes: [subscribeData],
                      action: "subscribe"
                    }
                  });
                }
              },
              {
                label: "Unsubscribe",
                onClick: () => {
                  const unsubscribeData = d?.sample?.sha1;
                  onToggleModal({
                    show: true,
                    name: ActionsModalNames.Unsubscribehashes,
                    modalData: {
                      subscriptionHashes: [unsubscribeData],
                      action: "unsubscribe"
                    }
                  });
                }
              }
            );
          }

          return (
            <ActionsMenu
              className="samples-grid__actions-column"
              id={`extracted-files-grid__action-menu-${idx}`}
              actions={actions}
              disabledActionDefaultMessage="Action unavailable for this sample."
            />
          );
        }
      }
    ]
  };

  return (
    <>
      <SortablePaginatedGrid
        schema={gridSchema}
        data={results}
        sort={sort}
        onSortChange={onSortChange}
        numPages={Math.ceil(count / pageSize)}
        onPageChange={onPageChange}
        page={page}
        pageSize={pageSize}
        showLoading={isLoading}
        totalResults={count}
        gridErrorMsg={gridErrorMsg}
        showPageNumbers
        selectableRows
        GridRowComponent={GridRowExpandable}
        checkRowSelected={checkRowSelected}
        onRowSelectChange={handleSelectRowChange}
        allRowsSelected={selectApi.isPageSelected(results, page)}
        expandedRows={expandedRows}
        createSelectedRowKey={createSelectedRowKey}
        selectedRows={selected}
        onSelectAllRowsChange={handleSelectPage}
        onRowExpandedChange={handleRowExpand}
        checkRowExpanded={handleCheckRowExpanded}
        emptyGridMsg={emptyGridMsg}
        renderExpandedRow={(value: ExtractedFilesResultItem) => (
          <Suspense
            fallback={
              <div className="expanded-details__loading-container">
                <Icon name="loading" />
              </div>
            }
          >
            <ExpandedRow sha1={value?.sample?.sha1} location="local" />
          </Suspense>
        )}
        LoadingComponent={<LoadingIndicator style={{ margin: "5rem 0" }} />}
      />
      <RenderWhen condition={Boolean(modal.activeModal)}>
        <ActionsModal
          modalName={modal.activeModal}
          activeModalData={modal.activeModalData}
          toggleModal={onToggleModal}
          onSubmit={onClassificationChange}
        />
      </RenderWhen>
      <RenderWhen condition={showTagsModal}>
        <TagsModal
          show={showTagsModal}
          handleShow={toggleTagsModal}
          onFormSubmit={handleTagAll}
        />
      </RenderWhen>
    </>
  );
};

export default ExtractedFilesTable;
